<script>
import Layout from "@/router/layouts/main";
import { api } from "@/state/services";
import { mapState } from "vuex";
import { VclList } from "vue-content-loading";
import Editor from "@tinymce/tinymce-vue";
import { required } from "vuelidate/lib/validators";
import { isEmbeddedWebView } from "@/utils/webview";
import native from "@/native";

const richTextRequired = (value) => {
  if (!value) {
    return false;
  }

  if (/<img[\s\S]*?>/i.test(value)) {
    return true;
  }

  const plainText = value
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

  return plainText.length >= 2;
};

export default {
  locales: {
    pt: {
      Tickets: "Tickets",
      "New Ticket": "Novo Ticket",
      Subject: "Assunto",
      "Subject is required.": "O assunto e obrigatorio.",
      Message: "Mensagem",
      "Message is required.": "A mensagem e obrigatoria.",
      Send: "Enviar",
      Date: "Data",
      Status: "Status",
      View: "Ver",
      "We apologize, we are unable to retrieve information at this time. Please try again later.":
        "Pedimos desculpas, nao podemos recuperar informacoes no momento. Por favor, tente novamente mais tarde.",
      "No records found.": "Nenhum registro encontrado."
    },
    es: {
      Tickets: "Tickets",
      "New Ticket": "Nuevo Ticket",
      Subject: "Asunto",
      "Subject is required.": "El asunto se requiere.",
      Message: "Mensaje",
      "Message is required.": "El mensaje se requiere.",
      Send: "Enviar",
      Date: "Data",
      Status: "Status",
      View: "View",
      "We apologize, we are unable to retrieve information at this time. Please try again later.":
        "Nos disculpamos, no podemos recuperar informacion en este momento. Por favor, intentelo de nuevo mas tarde.",
      "No records found.": "No se encontraron registros."
    }
  },
  components: {
    Layout,
    VclList,
    Editor
  },
  data() {
    return {
      body: null,
      loading: true,
      errored: false,
      empty: false,
      isEmbeddedWebView: false,
      // `isNativeApp` é mais restrito que `isEmbeddedWebView`: só é verdadeiro
      // no aplicativo oficial (Capacitor), onde existe câmera nativa.
      isNativeApp: false,
      nativePhotoError: "",

      ticket: {
        modal: false,
        loading: false,
        alert: {
          type: "",
          message: ""
        },
        sector: 7,
        subject: "",
        description: "",
        descriptionText: "",
        descriptionImages: []
      },

      editorInit: {
        menubar: false,
        branding: false,
        height: 320,
        paste_data_images: true,
        automatic_uploads: false,
        image_title: true,
        file_picker_types: "image",
        plugins: [
          "advlist autolink lists link image charmap preview anchor",
          "searchreplace visualblocks code fullscreen",
          "insertdatetime media table paste help wordcount"
        ],
        toolbar:
          "undo redo | blocks | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist | link image | removeformat code",
        file_picker_callback: (callback) => {
          const input = document.createElement("input");
          input.setAttribute("type", "file");
          input.setAttribute("accept", "image/*");

          input.addEventListener("change", () => {
            const file = input.files && input.files[0];

            if (!file) {
              return;
            }

            const reader = new FileReader();
            reader.addEventListener("load", () => {
              callback(reader.result, { title: file.name });
            });
            reader.readAsDataURL(file);
          });

          input.click();
        }
      }
    };
  },
  computed: {
    ...mapState({
      account: (state) => state.account
    })
  },
  validations: {
    ticket: {
      subject: { required },
      description: { required: richTextRequired }
    }
  },
  methods: {
    detectEmbeddedWebView() {
      return isEmbeddedWebView();
    },
    escapeHtml(value) {
      return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    syncWebViewDescription() {
      const textHtml = this.escapeHtml(this.ticket.descriptionText)
        .replace(/\n/g, "<br>")
        .trim();

      const imageHtml = this.ticket.descriptionImages
        .map((image) => `<p><img src="${image.src}" alt="${this.escapeHtml(image.name)}" /></p>`)
        .join("");

      const blocks = [];

      if (textHtml) {
        blocks.push(`<p>${textHtml}</p>`);
      }

      if (imageHtml) {
        blocks.push(imageHtml);
      }

      this.ticket.description = blocks.join("");
    },
    handleWebViewImages(event) {
      const files = Array.from((event.target && event.target.files) || []);

      if (!files.length) {
        return;
      }

      files.forEach((file) => {
        if (!file.type || !file.type.startsWith("image/")) {
          return;
        }

        const reader = new FileReader();
        reader.addEventListener("load", () => {
          this.ticket.descriptionImages.push({
            name: file.name,
            src: reader.result
          });
          this.syncWebViewDescription();
          this.$v.ticket.description.$touch();
        });
        reader.readAsDataURL(file);
      });

      event.target.value = "";
    },
    /**
     * Anexa foto pela CÂMERA NATIVA (só no aplicativo oficial).
     *
     * Por que não seguir com o `input[type=file]`: o caminho antigo foi escrito
     * para o aplicativo de terceiro em React Native e testava
     * `window.ReactNativeWebView`, que não existe no nosso container. Além
     * disso, há um bug conhecido do Capacitor em que alguns aparelhos Android
     * abrem o seletor de arquivos em vez da câmera mesmo com `capture`.
     *
     * De quebra, usar a câmera nativa é uso comprovado de API do sistema — um
     * dos argumentos concretos contra a reprovação pela regra 4.2 da Apple.
     */
    async addNativePhoto(source) {
      this.nativePhotoError = "";

      try {
        const foto = await native.camera.pickPhoto({ source: source, quality: 70, width: 1600 });

        // Usuário cancelou: caminho normal, nada a fazer.
        if (!foto || !foto.dataUrl) {
          return;
        }

        const extensao = foto.format || "jpg";

        this.ticket.descriptionImages.push({
          name: "foto-" + Date.now() + "." + extensao,
          src: foto.dataUrl
        });

        this.syncWebViewDescription();
        this.$v.ticket.description.$touch();
        native.haptic("success");
      } catch (error) {
        this.nativePhotoError =
          "Nao foi possivel abrir a camera. Verifique a permissao do aplicativo nas configuracoes do aparelho.";
      }
    },
    removeWebViewImage(index) {
      this.ticket.descriptionImages.splice(index, 1);
      this.syncWebViewDescription();
      this.$v.ticket.description.$touch();
    },
    resetTicketForm() {
      this.ticket.sector = 7;
      this.ticket.subject = "";
      this.ticket.description = "";
      this.ticket.descriptionText = "";
      this.ticket.descriptionImages = [];
      this.ticket.loading = false;
      this.ticket.alert.type = "";
      this.ticket.alert.message = "";
      this.$v.ticket.$reset();
    },
    getTickets() {
      api
        .get("ticket")
        .then((response) => {
          if (response.data.status == "success") {
            this.body = response.data.list;
          } else {
            this.body = null;
          }
        })
        .catch((error) => {
          this.errored = error;
        })
        .finally(() => {
          this.loading = false;
          if (this.body == "" || this.body == null) {
            this.empty = true;
          }
        });
    },
    ticketSubmit() {
      if (this.isEmbeddedWebView) {
        this.syncWebViewDescription();
      }

      this.$v.ticket.$touch();

      if (!this.$v.ticket.$error && this.ticket.subject && this.ticket.description) {
        this.ticket.loading = true;

        api
          .post("ticket", {
            sector: this.ticket.sector,
            subject: this.ticket.subject,
            description: this.ticket.description
          })
          .then((response) => {
            if (response.data.status == "success") {
              this.resetTicketForm();
              this.ticket.alert.type = "alert-success";
              this.ticket.alert.message = "The ticket has been created, wait for contact.";
              this.ticket.modal = false;
              this.$router.push("/tickets/" + response.data.ticket.id);
            }
          })
          .catch((error) => {
            this.ticket.errored = error;
            this.ticket.alert.type = "alert-danger";
            this.ticket.alert.message = "The ticket could not be created, please try again.";
            this.ticket.loading = false;
          });
      }
    }
  },
  mounted() {
    this.isNativeApp = native.isNative;
    this.isEmbeddedWebView = this.detectEmbeddedWebView();
    this.getTickets();
  }
};
</script>

<template>
  <Layout>
    <div class="page-title-box d-md-flex justify-content-between mb-3">
      <div>
        <h3>{{ t("Tickets") }}</h3>
        <p></p>
      </div>
    </div>

    <div class="filter-bar d-flex mb-4">
      <b-dropdown variant="outline-light">
        <template #button-content>
          Acoes
          <svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="m12 14.168-5.582-5.61L5 9.967 12 17l7-7-1.415-1.414L12 14.168Z" fill="currentColor"></path>
          </svg>
        </template>
        <b-dropdown-item @click="ticket.modal = true">{{ t("New Ticket") }}</b-dropdown-item>
      </b-dropdown>
    </div>

    <b-modal v-model="ticket.modal" :title="t('New Ticket')" modal-class="modal-right" hide-footer @hidden="resetTicketForm">
      <b-form @submit.prevent="ticketSubmit">
        <div v-if="ticket.alert.message" :class="'alert ' + ticket.alert.type">{{ ticket.alert.message }}</div>

        <b-form-group id="ticket-subject" :label="t('Subject')" label-for="ticket-subject">
          <b-form-input
            id="ticket-subject"
            v-model="ticket.subject"
            type="text"
            :class="{ 'is-invalid': $v.ticket.subject.$error }"
            autocomplete="off"
          ></b-form-input>
          <div v-if="$v.ticket.subject.$error" class="invalid-feedback">
            <span v-if="!$v.ticket.subject.required">O assunto e obrigatorio</span>
          </div>
        </b-form-group>

        <b-form-group id="ticket-description" :label="t('Message')" label-for="ticket-description">
          <div v-if="isEmbeddedWebView">
            <b-form-textarea
              id="ticket-description"
              v-model="ticket.descriptionText"
              rows="6"
              max-rows="10"
              placeholder="Descreva seu chamado"
              @input="syncWebViewDescription"
            ></b-form-textarea>

            <!--
              No aplicativo oficial usamos a camera/galeria nativa; em qualquer
              outro WebView (inclusive o aplicativo de terceiro) continua
              valendo o input de arquivo de sempre.
            -->
            <div v-if="isNativeApp" class="mt-3">
              <button type="button" class="btn btn-outline-default btn-sm mr-2" @click="addNativePhoto('camera')">
                Tirar foto
              </button>
              <button type="button" class="btn btn-outline-default btn-sm" @click="addNativePhoto('photos')">
                Escolher da galeria
              </button>
              <div v-if="nativePhotoError" class="text-danger small mt-2">{{ nativePhotoError }}</div>
            </div>

            <div v-else class="mt-3">
              <label class="btn btn-outline-default btn-sm mb-0">
                Adicionar imagem
                <input class="d-none" type="file" accept="image/*" multiple @change="handleWebViewImages" />
              </label>
            </div>

            <div v-if="ticket.descriptionImages.length" class="ticket-image-list mt-3">
              <div v-for="(image, index) in ticket.descriptionImages" :key="`${image.name}-${index}`" class="ticket-image-item">
                <img :src="image.src" :alt="image.name" class="ticket-image-preview" />
                <div class="ticket-image-meta">
                  <small class="d-block text-truncate">{{ image.name }}</small>
                  <button type="button" class="btn btn-link btn-sm p-0 text-danger" @click="removeWebViewImage(index)">Remover</button>
                </div>
              </div>
            </div>

            <small class="form-text text-muted">No aplicativo embutido usamos um modo compativel para digitar e anexar imagens.</small>
          </div>

          <div v-else>
            <Editor
              id="ticket-description"
              v-model="ticket.description"
              api-key="nkq975n9jocihq9zgkra19sr3cyx7j1q6stp3tc33ix0hcsw"
              :init="editorInit"
            />
            <small class="form-text text-muted">Voce pode escrever com formatacao e inserir imagens junto da mensagem.</small>
          </div>

          <div v-if="$v.ticket.description.$error" class="invalid-feedback d-block">
            <span v-if="!$v.ticket.description.required">A mensagem e obrigatoria</span>
          </div>
        </b-form-group>

        <b-button :disabled="ticket.loading == true || this.ticket.subject.length < 2 || $v.ticket.$error" type="submit" variant="default">
          Enviar
          <b-spinner v-if="ticket.loading" small class="ml-2 align-middle" variant="white" role="status"></b-spinner>
        </b-button>
      </b-form>
      <template v-slot:modal-footer>
        <div></div>
      </template>
    </b-modal>

    <div class="card">
      <div class="card-body">
        <div v-if="errored">{{ t("We apologize, we are unable to retrieve information at this time. Please try again later.") }}</div>
        <div v-else-if="empty">{{ t("No records found.") }}</div>
        <div v-else class="table-responsive">
          <vcl-list v-if="loading" class="col-lg-6"></vcl-list>
          <!--
            `table-cards` empilha cada ticket como um card abaixo de md (ver
            design/custom/components/_table.scss). Como esta e uma tabela crua,
            e nao um <b-table>, o rotulo de cada celula vem do `data-label`
            escrito aqui — a prop `stacked` so existe no b-table.
          -->
          <table v-else class="table table-centered table-nowrap table-cards">
            <thead class="thead-light">
              <tr>
                <!-- Espelha o `d-none d-md-table-cell` da celula do avatar, para
                     cabecalho e corpo nao saírem de sincronia se o breakpoint
                     mudar depois. -->
                <th class="d-none d-md-table-cell"></th>
                <th>#</th>
                <th>Assunto</th>
                <th>Data</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(td, index) in body" :key="index">
                <!--
                  O avatar e sempre o do proprio usuario logado, igual em todas
                  as linhas: no celular ele so consumiria uma linha inteira do
                  card sem informar nada.
                -->
                <td class="d-none d-md-table-cell">
                  <img v-if="account.user.avatar" class="rounded-circle header-profile-user" :src="account.user.avatar" alt="" />
                  <img
                    v-else
                    class="rounded-circle header-profile-user ml-2"
                    :src="'https://ui-avatars.com/api/?background=27416E&color=fff&name=' + account.user.name"
                    alt=""
                  />
                </td>
                <td class="notranslate" data-label="#">{{ td.id }}</td>
                <td class="notranslate" data-label="Assunto">{{ td.subject }}</td>
                <td class="notranslate" data-label="Data"><i class="mdi mdi-clock-outline font-size-18 align-middle"></i> {{ td.date }}</td>
                <td data-label="Status">{{ td.status }}</td>
                <td class="text-right">
                  <router-link :to="'/tickets/' + td.id" class="btn btn-default btn-sm btn-rounded">
                    {{ t("View") }}
                    <i class="mdi mdi-arrow-right ml-1"></i>
                  </router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style>
.tox-tinymce {
  border-color: #d2d2e4 !important;
  border-radius: 5px !important;
}

.tox .tox-toolbar,
.tox .tox-toolbar__overflow,
.tox .tox-toolbar__primary {
  background: url("data:image/svg+xml;charset=utf8,%3Csvg height='39px' viewBox='0 0 40 39px' width='40' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='0' y='38px' width='100' height='1' fill='%23D2D2E4'/%3E%3C/svg%3E") left 0 top 0 #fff !important;
}

.tox:not([dir=rtl]) .tox-toolbar__group:not(:last-of-type) {
  border-color: #d2d2e4 !important;
}

.tox .tox-statusbar {
  border-color: #d2d2e4 !important;
}

.tox-statusbar__branding {
  display: none !important;
}

.ticket-image-list {
  display: grid;
  gap: 12px;
}

.ticket-image-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border: 1px solid #d2d2e4;
  border-radius: 8px;
}

.ticket-image-preview {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 8px;
  background: #f5f6fa;
}

.ticket-image-meta {
  min-width: 0;
  flex: 1;
}
</style>
